import { useState, useCallback, useMemo } from 'react';
import { BulkSelectionPayload } from '../server/bulk/bulkTypes';

export interface UseBulkSelectionReturn {
  selectionMode: 'ALL' | 'EXPLICIT';
  selectedIds: string[];
  excludedIds: string[];
  selectedCount: number;
  isAllSelected: boolean;
  isPropertySelected: (id: string) => boolean;
  toggleSelectProperty: (id: string) => void;
  toggleSelectAll: () => void;
  clearSelection: () => void;
  getSelectionPayload: (filterPayload?: any) => BulkSelectionPayload;
}

export function useBulkSelection(totalCount: number): UseBulkSelectionReturn {
  const [selectionMode, setSelectionMode] = useState<'ALL' | 'EXPLICIT'>('EXPLICIT');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);

  const isPropertySelected = useCallback(
    (id: string): boolean => {
      if (selectionMode === 'ALL') {
        return !excludedIds.includes(id);
      }
      return selectedIds.includes(id);
    },
    [selectionMode, selectedIds, excludedIds]
  );

  const toggleSelectProperty = useCallback(
    (id: string) => {
      if (selectionMode === 'ALL') {
        setExcludedIds((prev) =>
          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
      } else {
        setSelectedIds((prev) =>
          prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
      }
    },
    [selectionMode]
  );

  const toggleSelectAll = useCallback(() => {
    if (selectionMode === 'ALL' && excludedIds.length === 0) {
      // Si ya estaban todos seleccionados al 100%, desmarcar todo
      setSelectionMode('EXPLICIT');
      setSelectedIds([]);
      setExcludedIds([]);
    } else {
      // Marcar absolutamente todos los resultados
      setSelectionMode('ALL');
      setExcludedIds([]);
      setSelectedIds([]);
    }
  }, [selectionMode, excludedIds]);

  const clearSelection = useCallback(() => {
    setSelectionMode('EXPLICIT');
    setSelectedIds([]);
    setExcludedIds([]);
  }, []);

  const selectedCount = useMemo(() => {
    if (selectionMode === 'ALL') {
      return Math.max(0, totalCount - excludedIds.length);
    }
    return selectedIds.length;
  }, [selectionMode, totalCount, excludedIds.length, selectedIds.length]);

  const isAllSelected = useMemo(() => {
    if (totalCount === 0) return false;
    if (selectionMode === 'ALL') {
      return excludedIds.length === 0;
    }
    return selectedIds.length === totalCount;
  }, [selectionMode, totalCount, excludedIds.length, selectedIds.length]);

  const getSelectionPayload = useCallback(
    (filterPayload?: any): BulkSelectionPayload => {
      return {
        mode: selectionMode,
        selectedIds: selectionMode === 'EXPLICIT' ? selectedIds : undefined,
        excludedIds: selectionMode === 'ALL' ? excludedIds : undefined,
        filters: filterPayload,
      };
    },
    [selectionMode, selectedIds, excludedIds]
  );

  return {
    selectionMode,
    selectedIds,
    excludedIds,
    selectedCount,
    isAllSelected,
    isPropertySelected,
    toggleSelectProperty,
    toggleSelectAll,
    clearSelection,
    getSelectionPayload,
  };
}
